import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  styled,
  Typography,
} from '@mui/material';

import { UploadFileResult } from 'hooks/ui/useFileUpload';
import TransactionPreview from './TransactionPreview';
import Button from 'components/Button';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Column, Row } from 'further-ui/components/Layout';

const RadioLabel = styled(FormControlLabel)(({ theme }) => ({
  width: '400px',
  border: `1px solid ${theme.palette.grey[300]}`,
  padding: theme.spacing(theme.spacingSizes.xxs, theme.spacingSizes.xs),

  '& .MuiFormControlLabel-label': {
    fontSize: theme.fontSizes.regular,
  },
}));

const DateFormatContainer = styled(RadioGroup)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
}));

const Label = styled(FormLabel)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: theme.fontSizes.lg,
  color: theme.palette.grey[600],
  marginBottom: theme.spacing(theme.spacingSizes.xs),
}));

type Props = {
  uploadedFile: UploadFileResult;
  onNextStep: () => void;
  onPreviousStep: () => void;
  onDateFormatChange: (dateFormat: string) => void;
  dateFormat?: string;
  bankAccountId: string;
};

const dateFormatOptions = [
  { label: 'dd/mm/yyyy', value: 'dd/MM/yyyy' },
  { label: 'mm/dd/yyyy', value: 'MM/dd/yyyy' },
  { label: 'dd/mm/yy', value: 'dd/MM/yy' },
  { label: 'mm/dd/yy', value: 'MM/dd/yy' },
];

const Settings: React.FC<Props> = ({
  uploadedFile,
  onNextStep,
  onPreviousStep,
  dateFormat,
  onDateFormatChange,
  bankAccountId,
}) => {
  const { selectedVehicleId } = useLayoutContext();

  const { data, isLoading } = api.bankStatement.getPreview.useQuery(
    {
      bankAccountId,
      vehicleId: selectedVehicleId ?? '',
      dateFormat: dateFormat ?? '',
      bankStatementFilePath: uploadedFile.filePath,
      page: 1,
      rowsPerPage: 1,
      order: 'asc',
      orderBy: 'date',
    },
    {
      enabled: !!dateFormat,
    },
  );

  const handleNextStep = () => {
    if (!data?.totalCount) {
      return;
    }

    onNextStep();
  };

  const operationPreview = data?.operations[0];

  return (
    <Column spacing="lg">
      <Column spacing="sm">
        <Typography variant="h4">Set up transactions for import</Typography>
        <Typography variant="body1" color="grey.600">
          Select the date format used in your bank statement. This will be
          applied to transactions you’re importing.
        </Typography>
      </Column>
      <FormControl>
        <Label id="date-format-label">Select date format</Label>
        <DateFormatContainer
          aria-labelledby="date-format-label"
          name="date-format"
          onChange={(e) => onDateFormatChange(e.target.value)}
          value={dateFormat}
        >
          {dateFormatOptions.map((dateFormat) => (
            <RadioLabel
              key={dateFormat.value}
              value={dateFormat.value}
              control={<Radio />}
              label={dateFormat.label}
            />
          ))}
        </DateFormatContainer>
      </FormControl>
      <Column spacing="sm">
        <Typography variant="h4">Transaction preview</Typography>
        {isLoading ? (
          <CircularProgress />
        ) : (
          !!dateFormat && <TransactionPreview {...(operationPreview ?? {})} />
        )}
      </Column>
      <Row centered spacing="sm" justify="flex-end">
        <Button variant="outlined" onClick={onPreviousStep} size="large">
          Back
        </Button>
        <Button
          variant="contained"
          disabled={!dateFormat || !data?.totalCount}
          onClick={handleNextStep}
          size="large"
        >
          Next
        </Button>
      </Row>
    </Column>
  );
};

export default Settings;
